import { css } from 'styled-components'
import { createMuiTheme } from '@material-ui/core/styles'
import { nlNL } from '@material-ui/core/locale'
import { themes } from 'config'

const sizes = themes.ucc.metrics

export const media = Object.keys(sizes).reduce((accumulator, label) => {
  const emSize = sizes[label] / 16
  accumulator[label] = (...args) => css`
    @media (min-width: ${emSize}em), print {
      ${css(...args)};
    }
  `
  return accumulator
}, {})

export function getMuiTheme(theme) {
  const isOutlinedInput = Boolean(theme.colors.inputBorder)
  const inputBg =
    theme.colors.inputBackground || theme.colors.fieldBackground

  return createMuiTheme(
    {
      overrides: {
        MuiInput: {
          root: {
            ...(isOutlinedInput && {
              backgroundColor: inputBg,
              borderRadius: 64,
              outline: `1px solid ${theme.colors.inputBorder}`,
              outlineOffset: '-1px',
              padding: '0 20px',
              transition: 'outline-color 150ms ease, outline-width 150ms ease',
              '&$focused': {
                outline: `2px solid ${theme.colors.inputBorderFocus}`,
                outlineOffset: '-2px',
              },
            }),
          },
          input: {
            ...(theme.colors.inputText && { color: theme.colors.inputText }),
            '&::placeholder': {
              color: theme.colors.inputPlaceholder,
              opacity: 1,
            },
          },
          underline: isOutlinedInput
            ? {
                '&:before': { borderBottom: 'none' },
                '&:after': { borderBottom: 'none' },
                '&:hover:not($disabled):before': { borderBottom: 'none' },
                '&$disabled:before': { borderBottomStyle: 'none' },
              }
            : {
                '&:before': {
                  borderBottom: `1px solid ${theme.colors.inputLabel}`,
                },
                '&:hover:not($disabled):before': {
                  borderBottom: `1px solid ${theme.colors.brandBravo}`,
                },
                '&$disabled:before': {
                  borderBottom: 'none',
                  borderBottomStyle: 'none',
                },
              },
        },
        MuiInputLabel: {
          root: {
            '&::first-letter': {
              textTransform: 'uppercase',
            },
          },
          filled: {
            ...(isOutlinedInput && {
              transform: 'translate(20px, 20px) scale(1)',
            }),
            '&$shrink': {
              transform: isOutlinedInput
                ? 'translate(20px, 10px) scale(0.86)'
                : 'translate(12px, 10px) scale(0.86)',
            },
            '&$error': {
              color: theme.colors.inputPlaceholder,
            },
          },
          shrink: {
            transform: 'translate(0, 1.5px) scale(0.86)',
            transformOrigin: 'top left',
          },
        },
        MuiInputAdornment: {
          root: {
            flex: '0 0 auto',
          },
        },
        MuiFormLabel: {
          root: {
            '&$focused': {
              color: theme.colors.inputPlaceholder,
            },
          },
        },
        MuiFormControlLabel: {
          // root: {
          //   marginLeft: 0,
          // },
          label: {
            display: 'block',
            width: '100%',
          },
        },
        MuiFilledInput: {
          root: {
            backgroundColor: inputBg,
            ...(isOutlinedInput
              ? {
                  borderTopLeftRadius: 64,
                  borderTopRightRadius: 64,
                  borderBottomLeftRadius: 64,
                  borderBottomRightRadius: 64,
                  outline: `1px solid ${theme.colors.inputBorder}`,
                  outlineOffset: '-1px',
                  transition:
                    'outline-color 150ms ease, outline-width 150ms ease',
                }
              : {
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                }),
            '&:hover': {
              backgroundColor: isOutlinedInput
                ? inputBg
                : theme.colors.isolatedBackground,
            },
            '&$focused': {
              backgroundColor: isOutlinedInput
                ? inputBg
                : theme.colors.brandDelta,
              ...(isOutlinedInput && {
                outline: `2px solid ${theme.colors.inputBorderFocus}`,
                outlineOffset: '-2px',
              }),
            },
            '&$disabled': {
              backgroundColor: theme.colors.disabledInputBackground,
            },
            '&$disabled:hover': {
              backgroundColor: theme.colors.disabledInputBackground,
            },
            '&$error': {
              backgroundColor: theme.colors.errorInputBackground,
            },
          },
          input: {
            ...(theme.colors.inputText && { color: theme.colors.inputText }),
            ...(isOutlinedInput && {
              paddingLeft: 20,
              paddingRight: 20,
            }),
            '&::placeholder': {
              color: theme.colors.inputPlaceholder,
              opacity: 1,
            },
          },
          inputMultiline: {
            ...(isOutlinedInput && {
              padding: 20,
            }),
          },
          multiline: {
            ...(isOutlinedInput && {
              padding: 0,
              borderTopLeftRadius: 32,
              borderTopRightRadius: 32,
              borderBottomLeftRadius: 32,
              borderBottomRightRadius: 32,
              overflow: 'hidden',
            }),
          },
          underline: isOutlinedInput
            ? {
                '&:before': { borderBottom: 'none' },
                '&:after': { borderBottom: 'none' },
                '&:hover:not($disabled):before': { borderBottom: 'none' },
                '&$disabled:before': { borderBottomStyle: 'none' },
              }
            : {
                '&:before': {
                  borderBottom: `1px solid ${theme.colors.inputLabel}`,
                },
                '&:hover:not($disabled):before': {
                  borderBottom: `1px solid ${theme.colors.brandBravo}`,
                },
                '&$disabled:before': {
                  borderBottom: 'none',
                  borderBottomStyle: 'none',
                },
              },
        },
        ...(theme.shadows.popup && {
          MuiMenu: {
            paper: {
              borderRadius: 16,
              boxShadow: theme.shadows.popup,
            },
          },
          MuiPopover: {
            paper: {
              borderRadius: 16,
              boxShadow: theme.shadows.popup,
            },
          },
        }),
        ...(theme.dialogBorderRadius && {
          MuiDialog: {
            paper: {
              borderRadius: theme.dialogBorderRadius,
              backgroundColor:
                theme.colors.dialogBackground ||
                theme.colors.isolatedBackground,
            },
          },
        }),
        ...(theme.colors.dialogBackdrop && {
          MuiBackdrop: {
            root: {
              backgroundColor: theme.colors.dialogBackdrop,
            },
          },
        }),
        MuiMenuItem: {
          root: {
            ...(theme.colors.menuItemHoverBackground && {
              borderRadius: 64,
              margin: '2px 8px',
              '&:hover': {
                backgroundColor: theme.colors.menuItemHoverBackground,
              },
              '&$selected': {
                backgroundColor: theme.colors.menuItemHoverBackground,
              },
              '&$selected:hover': {
                backgroundColor: theme.colors.menuItemHoverBackground,
              },
            }),
          },
        },
        MuiSelect: {
          ...(isOutlinedInput && {
            select: {
              '&:focus': {
                backgroundColor: 'transparent',
                borderRadius: 64,
              },
            },
            // RDC: nudge the dropdown arrow 3px further from the right edge
            // (MUI's variant classes override the base `.MuiSelect-icon` so we
            // need to set each one explicitly).
            icon: { right: 10 },
            iconFilled: { right: 10 },
            iconOutlined: { right: 10 },
          }),
        },
        MuiCheckbox: {
          root: {
            color: theme.colors.text,
            ...(theme.colors.checkboxChecked && {
              '&.Mui-checked': {
                color: theme.colors.checkboxChecked,
              },
              '&.MuiCheckbox-indeterminate': {
                color: theme.colors.checkboxChecked,
              },
            }),
          },
          colorPrimary: {
            ...(theme.colors.checkboxChecked && {
              '&.Mui-checked': {
                color: theme.colors.checkboxChecked,
              },
            }),
          },
          colorSecondary: {
            ...(theme.colors.checkboxChecked && {
              '&.Mui-checked': {
                color: theme.colors.checkboxChecked,
              },
            }),
          },
        },
        MuiTypography: {
          root: {
            color: theme.colors.text,
          },
        },
      },
      palette: {
        primary: {
          main: theme.colors.brandAlfa,
        },
        secondary: {
          main: theme.colors.brandAlfa,
        },
        text: {
          primary: theme.colors.text,
          secondary: theme.colors.inputPlaceholder,
          disabled: theme.colors.textTonedDown,
          hint: theme.colors.brandAlfa,
        },
      },
      typography: {
        fontFamily: theme.font,
        fontSize: 12,
      },
    },
    nlNL,
  )
}
